"use client";

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Heart } from "lucide-react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import image from "../../assets/images/login.png";

// Add Montserrat font import
import "@fontsource/montserrat/300.css";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/700.css";
import apiEndpoints from "../../apiconfig/index.js";

const loginConfig = {
  initialState: {
    email: "",
    password: "",
    rememberMe: false,
    showPassword: false,
    loading: false,
    error: null,
  },
};

export default function Login() {
  const [formData, setFormData] = useState(loginConfig.initialState);
  const navigate = useNavigate();

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear any existing errors when user starts typing
    if (field === "email" || field === "password") {
      setFormData((prev) => ({ ...prev, error: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.email || !formData.password) {
      showSnackbar("Please fill in all fields", "error");
      setFormData((prev) => ({
        ...prev,
        error: "Please fill in all fields",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetch(apiEndpoints.adminLogin, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "login",
          email: formData.email,
          password: formData.password,
        }),
      });

      const result = await response.json();

      if (result.status === "success") {
        if (result.token) {
          localStorage.setItem("authToken", result.token);
        }
        if (result.user) {
          localStorage.setItem("user", JSON.stringify(result.user));

          if (result.user.permissions) {
            localStorage.setItem(
              "permissions",
              JSON.stringify(result.user.permissions),
            );
          }
        }

        showSnackbar(
          "Login successful! Redirecting to dashboard...",
          "success",
        );
        setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 1500);
      } else {
        const errorMsg = result.message || "Invalid email or password";
        setFormData((prev) => ({
          ...prev,
          loading: false,
          error: errorMsg,
        }));

        // Show error snackbar
        showSnackbar(errorMsg, "error");
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMsg = "Server error. Please try again.";
      setFormData((prev) => ({
        ...prev,
        loading: false,
        error: errorMsg,
      }));

      // Show error snackbar
      showSnackbar(errorMsg, "error");
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f8f9fb",
          p: { xs: 1, sm: 2 },
          overflow: "hidden",
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <Paper
          elevation={6}
          sx={{
            width: "100%",
            maxWidth: 1100,
            height: "100%",
            maxHeight: 700,
            p: 4,
            borderRadius: 4,
            boxShadow: "1 1 1 rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            fontFamily: "inherit",
          }}
        >
          {/* Left side - Login Form */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              height: "100%",
              pr: { md: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              fontFamily: "inherit",
            }}
          >
            <Box textAlign="center" mb={3}>
              <Box
                component="img"
                src="/logo.png"
                alt="Logo"
                sx={{
                  width: 300,
                  height: "auto",
                  mx: "auto",
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                style={{ marginTop: 8 }}
                sx={{
                  fontSize: { xs: "14px" },
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Sign in to your admin dashboard
              </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
              <TextField
                label="Email Address"
                type="email"
                style={{ marginBottom: 20 }}
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                fullWidth
                margin="normal"
                error={!!formData.error && !formData.email}
                InputProps={{
                  sx: { fontFamily: "'Montserrat', sans-serif" },
                }}
                InputLabelProps={{
                  sx: { fontFamily: "'Montserrat', sans-serif" },
                }}
              />

              <Box position="relative" mb={2}>
                <TextField
                  label="Password"
                  type={formData.showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  fullWidth
                  error={!!formData.error && !formData.password}
                  InputProps={{
                    sx: { fontFamily: "'Montserrat', sans-serif" },
                  }}
                  InputLabelProps={{
                    sx: { fontFamily: "'Montserrat', sans-serif" },
                  }}
                />
                <IconButton
                  onClick={() =>
                    handleInputChange("showPassword", !formData.showPassword)
                  }
                  sx={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  {formData.showPassword ? (
                    <Eye size={18} />
                  ) : (
                    <EyeOff size={18} />
                  )}
                </IconButton>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.rememberMe}
                      onChange={(e) =>
                        handleInputChange("rememberMe", e.target.checked)
                      }
                      color="primary"
                      sx={{
                        width: { xs: 20 },
                        height: { xs: 20 },
                        "& .MuiSvgIcon-root": { fontSize: { xs: 20 } },
                        pl: { xs: 2.5 },
                        pr: { xs: 2 },
                      }}
                    />
                  }
                  label="Remember me"
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: { xs: "13px" },
                      fontFamily: "'Montserrat', sans-serif",
                    },
                  }}
                />
                <Typography
                  component={Link}
                  to="/forgot-password"
                  variant="body2"
                  sx={{
                    color: "black",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                    fontSize: { xs: "13px", sm: "14px" },
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Forgot password?
                </Typography>
              </Box>

              {formData.error && (
                <Typography
                  variant="body2"
                  color="error"
                  sx={{
                    mb: 2,
                    fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  {formData.error}
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={formData.loading}
                sx={{
                  height: 44,
                  mb: 2,
                  backgroundColor: "black",
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 500,
                  "&:hover": { backgroundColor: "#333" },
                }}
              >
                {formData.loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            <Divider sx={{ my: 2 }} />

            {/* <Typography
              variant="body2"
              align="center"
              sx={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Don't have an account?{" "}
              <Typography
                component={Link}
                to="/register"
                sx={{
                  color: "black",
                  textDecoration: "none",
                  fontWeight: 500,
                  "&:hover": { textDecoration: "underline" },
                  fontFamily: "'Montserrat', sans-serif",
                }}
              >
                Create one
              </Typography>
            </Typography> */}
          </Box>

          <Box
            component="img"
            src={image}
            alt="Login illustration"
            sx={{
              width: { xs: "100%", md: "50%" },
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              objectFit: "fill",
              borderRadius: 2,
            }}
          />
        </Paper>
      </Box>

      {/* Snackbar for notifications - Positioned at top right */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiSnackbar-root": {
            top: "80px",
            right: "24px",
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          //variant="filled"
          sx={{
            width: "100%",
            fontFamily: "'Montserrat', sans-serif",
            alignItems: "center",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
